'use client';

import { useAppTranslation } from '@freedraw/i18n/react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ArrowRight } from 'lucide-react';

import { BRAND, brandTint } from '@/theme';

const INK = BRAND.ink;
const SUBTLE = '#4b4b52';
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace';

// Each line of the sample, split into coloured tokens.
const CODE_LINES: Array<Array<{ text: string; color?: string }>> = [
  [{ text: 'flowchart', color: BRAND.pinkStrong }, { text: ' TD' }],
  [{ text: '  A' }, { text: '[Idea]', color: '#2563eb' }, { text: ' --> ', color: '#8a8290' }, { text: 'B' }, { text: '{Review}', color: '#2563eb' }],
  [{ text: '  B' }, { text: ' -->|Yes| ', color: '#8a8290' }, { text: 'C' }, { text: '[Build]', color: '#2563eb' }],
  [{ text: '  B' }, { text: ' -->|No| ', color: '#8a8290' }, { text: 'D' }, { text: '[Revise]', color: '#2563eb' }],
  [{ text: '  C' }, { text: ' --> ', color: '#8a8290' }, { text: 'E' }, { text: '[Release]', color: '#2563eb' }]
];

function MermaidCodeCard({ label }: { label: string }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '14px',
        border: `1px solid ${brandTint(0.35)}`,
        bgcolor: '#fbf9fa',
        overflow: 'hidden',
        boxShadow: '0 12px 30px rgba(17, 24, 39, 0.06)'
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={{ px: 1.75, py: 1, borderBottom: `1px solid ${brandTint(0.25)}`, bgcolor: '#ffffff' }}
      >
        {[BRAND.pink, '#f5d0dd', '#ece6ea'].map((color) => (
          <Box key={color} sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: color }} />
        ))}
        <Typography sx={{ pl: 1, fontSize: 12, fontWeight: 700, color: SUBTLE }}>{label}</Typography>
      </Stack>
      <Box
        component="pre"
        sx={{ m: 0, px: 2, py: 1.75, fontFamily: MONO, fontSize: { xs: 12, sm: 13 }, lineHeight: 1.75, overflowX: 'auto' }}
      >
        {CODE_LINES.map((tokens, lineIndex) => (
          <Box key={lineIndex} component="span" data-mermaid-line sx={{ display: 'block' }}>
            <Box component="span" sx={{ display: 'inline-block', width: 22, color: '#c4bac0', userSelect: 'none' }}>
              {lineIndex + 1}
            </Box>
            {tokens.map((token, tokenIndex) => (
              <Box key={tokenIndex} component="span" sx={{ color: token.color ?? INK }}>
                {token.text}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

// Hand-drawn rendering of the sample flowchart, matching the board's shape style.
function MermaidDiagramCard({ label }: { label: string }) {
  const box = { fill: '#ffffff', stroke: INK, strokeWidth: 1.6 } as const;
  const text = { fontSize: 13, fontWeight: 600, fill: INK, textAnchor: 'middle', dominantBaseline: 'central' } as const;
  return (
    <Paper
      elevation={0}
      role="img"
      aria-label={label}
      sx={{
        borderRadius: '14px',
        border: `1.5px solid ${BRAND.pink}`,
        bgcolor: '#ffffff',
        backgroundImage: `radial-gradient(${brandTint(0.3)} 1px, transparent 1px)`,
        backgroundSize: '16px 16px',
        p: { xs: 1.5, sm: 2 },
        boxShadow: '0 18px 44px rgba(194, 24, 91, 0.10)'
      }}
    >
      <Box component="svg" viewBox="0 0 320 250" sx={{ display: 'block', width: '100%', height: 'auto' }}>
        <defs>
          <marker id="landing-mermaid-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M0 0 L10 5 L0 10 z" fill={INK} />
          </marker>
        </defs>
        <g fill="none" stroke={INK} strokeWidth="1.5" markerEnd="url(#landing-mermaid-arrow)">
          <path data-mermaid-edge d="M160 44 L160 62" pathLength={1} />
          <path data-mermaid-edge d="M120 100 C 96 114, 82 122, 82 140" pathLength={1} />
          <path data-mermaid-edge d="M200 100 C 224 114, 238 122, 238 140" pathLength={1} />
          <path data-mermaid-edge d="M82 176 L82 200" pathLength={1} />
        </g>
        <g data-mermaid-node>
          <rect x="112" y="12" width="96" height="32" rx="9" {...box} fill={BRAND.pinkLight} stroke={BRAND.pinkStrong} />
          <text x="160" y="28" {...text}>Idea</text>
        </g>
        <g data-mermaid-node>
          <path d="M160 62 L216 86 L160 110 L104 86 Z" {...box} />
          <text x="160" y="86" {...text}>Review</text>
        </g>
        <text x="90" y="116" fontSize="11" fill={SUBTLE} textAnchor="middle">Yes</text>
        <text x="230" y="116" fontSize="11" fill={SUBTLE} textAnchor="middle">No</text>
        <g data-mermaid-node>
          <rect x="38" y="140" width="88" height="36" rx="9" {...box} />
          <text x="82" y="158" {...text}>Build</text>
        </g>
        <g data-mermaid-node>
          <rect x="194" y="140" width="88" height="36" rx="9" {...box} />
          <text x="238" y="158" {...text}>Revise</text>
        </g>
        <g data-mermaid-node>
          <rect x="30" y="200" width="104" height="36" rx="9" {...box} fill={BRAND.pink} stroke={BRAND.pinkStrong} />
          <text x="82" y="218" {...text}>Release</text>
        </g>
      </Box>
    </Paper>
  );
}

/** Mermaid text and the board diagram it becomes: stacked on phones, side by side above. */
export function MermaidDemo() {
  const { t } = useAppTranslation();
  return (
    <Stack
      data-mermaid-demo
      direction={{ xs: 'column', sm: 'row' }}
      alignItems="center"
      spacing={1.5}
      sx={{ width: '100%' }}
    >
      <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
        <MermaidCodeCard label={t('landing.mermaidCodeLabel')} />
      </Box>
      <Stack direction="row" justifyContent="center" aria-hidden="true" sx={{ flexShrink: 0 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '50%',
            bgcolor: BRAND.pink,
            color: INK,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: { xs: 'rotate(90deg)', sm: 'none' }
          }}
        >
          <ArrowRight size={17} strokeWidth={2.4} />
        </Box>
      </Stack>
      <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
        <MermaidDiagramCard label={t('landing.mermaidDiagramLabel')} />
      </Box>
    </Stack>
  );
}
